import "./ProductList.css";
import { Row, Col, Container, Button } from "react-bootstrap";
import {
  ProductCard,
  PaginationMenu,
  ModalMessage,
  LoadingSpinner,
} from "../../components";
import { useState, useEffect, useCallback, useRef } from "react";
import { fetchProductsApi } from "../../api/productsApi.js";
import { useSelector } from "react-redux";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { FilterMenu } from "../../components";

export default function ProductList() {
  // displays all products with the option of sorting and filtering
  // products are displayed with basic info like image, description, rating

  const upArrow = <i className="bi bi-chevron-up"></i>;
  const downArrow = <i className="bi bi-chevron-down"></i>;
  const location = useLocation();
  const navigate = useNavigate();

  const { categoryId } = useParams();
  const categories = useSelector((state) => state.categories) || {}; // eslint-disable-line react-hooks/exhaustive-deps
  // says need to meomize categories but useSelector already memoizes...

  const initialFiltersRef = useRef(
    {
      category: { children: categories, isRoot: true },
      price: 1000,
      ratings: [false, false, false, false, false],
      selectedAttributes: [],
      sortKey: "createdAt_-1",
      searchText: "",
    },
    [categories]
  );

  const initialPaginationStateRef = useRef(
    {
      pageNr: 1,
      totalNrOfPages: 1,
    },
    []
  );

  const [products, setProducts] = useState(null);
  const [availableAttributesSet, setAvailableAttributesSet] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [paginationState, setPaginationState] = useState({
    ...initialPaginationStateRef.current,
  });
  const [menuCollapsed, setMenuCollapsed] = useState(true);
  const [updateProductsList, setUpdateProductsList] = useState(false);

  const createRatingsString = useCallback((newRatings = null) => {
    let ratingsString = "";
    let ratings = newRatings || selectedFilters?.ratings;
    if (ratings.includes(true)) {
      //at least 1 rating filter applied -> create query param string
      ratings.forEach((value, idx) => {
        if (value === true) {
          if (!ratingsString) {
            ratingsString = (idx + 1).toString();
          } else {
            ratingsString = ratingsString + "," + (idx + 1).toString();
          }
        }
      });
    }
    return ratingsString;
  }, [selectedFilters?.ratings]);

  const createQueryString = (newParams) => {
    const { price, ratings, attributes, sortKey, searchText, pageNr } =
      newParams;
    const ratingsQueryParam = createRatingsString(ratings);
    const maxPriceQueryParam = price || selectedFilters?.price || "";
    let attributeIdsQueryParam;
    if (attributes) {
      attributeIdsQueryParam = attributes.join(",");
    } else {
      attributeIdsQueryParam = selectedFilters?.selectedAttributes.join(",");
    }
    const sortKeyQueryParam =
      sortKey === undefined ? selectedFilters?.sortKey : sortKey;
    const searchTextQueryParam =
      searchText === undefined ? selectedFilters?.searchText : searchText;
    const pageNrQueryParam = pageNr || paginationState?.pageNr || "";
    let queryString = new URLSearchParams({
      ...(ratingsQueryParam && { ratings: ratingsQueryParam }),
      ...(maxPriceQueryParam && { price: maxPriceQueryParam }),
      ...(attributeIdsQueryParam && { attributes: attributeIdsQueryParam }),
      ...(sortKeyQueryParam && { sortKey: sortKeyQueryParam }),
      ...(pageNrQueryParam && { pageNr: pageNrQueryParam }),
      ...(searchTextQueryParam && { searchText: searchTextQueryParam }),
    }).toString();
    return queryString;
  };

  const fetchProducts = useCallback(
    async (filters = null, pageNr = null) => {
      setIsLoading(true);
      const category = filters?.category?._id || "";
      //construct queryString
      const ratingsString = createRatingsString(filters?.ratings);
      const maxPrice = filters?.price || "";
      const selectedAttributeIdsString =
        filters?.selectedAttributes.join(",") || "";
      const sortString = filters?.sortKey || "";
      const searchText = filters?.searchText || "";

      try {
        const res = await fetchProductsApi(
          category,
          ratingsString,
          maxPrice,
          selectedAttributeIdsString,
          sortString,
          pageNr,
          searchText
        );
        return res;
      } catch (error) {
        setErrorMsg("Could not load data. Please try again later.");
      } finally {
        setIsLoading(false);
        window.scrollTo(0, 0);
      }
    },
    [createRatingsString]
  );

  const findCategoryByIdBFS = useCallback(
    (id) => {
      const queue = JSON.parse(JSON.stringify(categories));
      while (queue.length > 0) {
        const currCategory = queue.shift(); //popleft()
        if (currCategory._id === id) {
          return currCategory;
        }
        if (currCategory.children && currCategory.children.length > 0) {
          queue.push(...currCategory.children);
        }
      }
      return null;
    },
    [categories]
  );

  useEffect(() => {
    // Update the initialFilters and selectedFilters state variable once categories (from store) is available
    if (!selectedFilters?.category?._id) {
      if (categories?.length > 0) {
        // if(!selectedFilters?.category?._id){
        // no category selected -> show main categories
        const rootCategory = {
          children: JSON.parse(JSON.stringify(categories)),
          isRoot: true,
        };
        setSelectedFilters((prevFilters) => ({...prevFilters, category: rootCategory }));
        initialFiltersRef.current.category = JSON.parse(
          JSON.stringify(rootCategory)
        );
      }
    }
  }, [categories, selectedFilters?.category?._id, initialFiltersRef]);

  useEffect(() => {
    // update selectedFilters and paginationState state variables with values from query params
    const queryParams = new URLSearchParams(location.search);
    const searchText =
      queryParams.get("searchText") || initialFiltersRef.current.searchText;
    const sortKey =
      queryParams.get("sortKey") || initialFiltersRef.current.sortKey;
    const price = queryParams.get("price") || initialFiltersRef.current.price;
    let ratingsString = queryParams.get("ratings");
    let ratings = [...initialFiltersRef.current.ratings];
    if (ratingsString) {
      ratingsString
        .split(",")
        .map(Number)
        .forEach((rating) => {
          ratings[rating - 1] = true;
        });
    }
    let selectedAttributes;
    let selectedAttributesString = queryParams.get("attributes");
    if (selectedAttributesString) {
      selectedAttributes = selectedAttributesString.split(",");
    } else {
      selectedAttributes = [...initialFiltersRef.current.selectedAttributes];
    }
    //from path params, have categoryId, then search redux categories for populated data
    let categoryObj;
    if (categoryId) {
      categoryObj = findCategoryByIdBFS(categoryId);
    } else {
      categoryObj = JSON.parse(
        JSON.stringify(initialFiltersRef.current.category)
      );
    }
    const updatedFilters = {
      sortKey,
      price,
      ratings,
      selectedAttributes,
      category: categoryObj,
      searchText,
    };
    
    setSelectedFilters(updatedFilters);
    const pageNr =
      Number(queryParams.get("pageNr")) ||
      initialPaginationStateRef.current.pageNr;
      setPaginationState((prevState) => ({...prevState, pageNr}));
    // fetchProducts(updatedFilters, pageNr)
    //   .then((res) => {
    //     setProducts(res?.products);
    //     setAvailableAttributesSet(res?.uniqueAttributes);
    //     setPaginationState({
    //       pageNr: res?.currPageNr,
    //       totalNrOfPages: res?.totalNrOfPages,
    //     });
    //   })
    //   .catch((error) => {
    //     setErrorMsg("Could not load products. Please try again later.");
    //     setIsLoading(false);
    //   });
    setUpdateProductsList(true);
  }, [
    location.search,
    location.pathname,
    initialFiltersRef,
    initialPaginationStateRef,
    categoryId,
    findCategoryByIdBFS,
  ]);

  useEffect(() => {
    // fetch products only if updateProductsList is true
    if(updateProductsList){
      fetchProducts(selectedFilters, paginationState.pageNr)
      .then((res) => {
        setProducts(res?.products);
        setAvailableAttributesSet(res?.uniqueAttributes);
      setPaginationState({
        pageNr: res?.currPageNr,
        totalNrOfPages: res?.totalNrOfPages,
      });
    })
    .catch((error) => {
      setErrorMsg("Could not load products. Please try again later.");
      setIsLoading(false);
    });
    setUpdateProductsList(false);
  }
  }, [fetchProducts, updateProductsList, selectedFilters, paginationState.pageNr])

  const navigateToNewUrl = (newParams = null) => {
    let queryString = "";
    let categoryPathParam = "";
    if (newParams) {
      queryString = createQueryString(newParams);
      if (newParams?.category !== undefined) {
        categoryPathParam = newParams?.category
          ? `/category/${newParams.category}`
          : "";
      } else if (categoryId) {
        categoryPathParam = `/category/${selectedFilters.category._id}`;
      }
      // else {
      // categoryPathParam = "";
      // }
    }
    navigate({
      pathname: `/product-list${categoryPathParam}`,
      search: queryString,
    });
  };

  const handleSelectNewCategory = (newCategory) => {
    // let newSelection = { ...newCategory };
    // setSelectedFilters({ ...selectedFilters, category: newSelection });
    navigateToNewUrl({ category: newCategory?._id });
  };

  const handleReturnToParentCategory = () => {
    const parent = selectedFilters?.category?.parent || null;
    const parentId = parent ? findCategoryByIdBFS(parent)._id : "";
    navigateToNewUrl({ category: parentId });
  };

  const handleChangeSelectedAttributes = (id, checked) => {
    // if id -1, reset filter
    let selectedAttributes;
    if (id === -1) {
      selectedAttributes = [];
    } else {
      selectedAttributes = selectedFilters?.selectedAttributes ? [...selectedFilters.selectedAttributes] : [];
      if (checked) {
        selectedAttributes.push(id);
      } else {
        selectedAttributes = selectedAttributes.filter(
          (attrId) => attrId !== id
        );
      }
    }
    navigateToNewUrl({ attributes: selectedAttributes });
  };

  const handleChangeRating = (stars, checked) => {
    let updatedRatings;
    if (!stars) {
      //reset
      updatedRatings = [initialFiltersRef.current.ratings];
    } else {
      updatedRatings = [...selectedFilters.ratings];
      updatedRatings[stars - 1] = checked; //1 star at idx 0 etc
    }
    navigateToNewUrl({ ratings: updatedRatings });
  };

  const handleChangePrice = (newPrice) => {
    if (!newPrice) {
      // reset price filter
      navigateToNewUrl({ price: initialFiltersRef.current.price });
    } else {
      setSelectedFilters({ ...selectedFilters, price: newPrice });
    }
  };

  const handleSubmitPrice = () => {
    navigateToNewUrl({ price: selectedFilters?.price });
  };

  const handleChangeSortKey = (newSortKey) => {
    navigateToNewUrl({ sortKey: newSortKey });
  };

  const handleRemoveSearchText = () => {
    navigateToNewUrl({ searchText: "" });
  };

  const handleResetAllFilters = () => {
    navigateToNewUrl();
  };

  const handleChangePageNr = (newPageNr) => {
    if (newPageNr < 1) {
      newPageNr = 1;
    } else if (newPageNr > paginationState.totalNrOfPages) {
      newPageNr = paginationState.totalNrOfPages;
    }
    navigateToNewUrl({ pageNr: newPageNr });
  };

  const headingElement = (
    <div className="mt-2" style={{ textAlign: "left" }}>
      <h1>Shop Products</h1>
      <p
        style={{
          fontStyle: "italic",
          color: "#828282",
          minHeight: "1.5rem",
        }}
      >
        {selectedFilters?.category?.fullPath || " "}
      </p>
    </div>
  );

  return (
    // SORTING AND FILTERING OPTIONS
    <Container fluid>
      {errorMsg && (
        <ModalMessage
          type="danger"
          message={errorMsg}
          setErrorMsg={setErrorMsg}
        />
      )}
      <Row>
        <Col>
          <div className="filter-menu-header mt-5">{headingElement}</div>
          <Button
            variant="none"
            size="lg"
            onClick={() => setMenuCollapsed(!menuCollapsed)}
            style={{ width: "fit-content", padding: "0", fontWeight: "600" }}
            className="filter-menu-header mt-4"
          >
            Refine Your Search {menuCollapsed ? downArrow : upArrow}
          </Button>
        </Col>
      </Row>
      <Row className="filter-row">
        {/* FILTER MENU */}
        <FilterMenu
          defaultPrice={initialFiltersRef.current.price}
          menuCollapsed={menuCollapsed}
          setMenuCollapsed={setMenuCollapsed}
          availableAttributesSet={availableAttributesSet}
          handleRemoveSearchText={handleRemoveSearchText}
          handleChangeSortKey={handleChangeSortKey}
          handleSelectNewCategory={handleSelectNewCategory}
          handleReturnToParentCategory={handleReturnToParentCategory}
          handleChangePrice={handleChangePrice}
          handleSubmitPrice={handleSubmitPrice}
          handleChangeRating={handleChangeRating}
          selectedFilters={selectedFilters}
          handleChangeSelectedAttributes={handleChangeSelectedAttributes}
          handleResetAllFilters={handleResetAllFilters}
          upArrow={upArrow}
        />

        <Col md={9}>
          {isLoading && <LoadingSpinner />}
          <div className="filter-menu-header-full-screen">{headingElement}</div>
          <Row xs={1} sm={2} lg={3} xxl={4} className="mt-3">
            {/* PRODUCT LIST ITEMS*/}
            {products &&
              products.map((product, idx) => {
                return <ProductCard key={idx} product={product} />;
              })}
          </Row>
          {/* PAGINATION MENU */}
          {products && products.length > 0 ? (
            <PaginationMenu
              pageNr={paginationState.pageNr}
              totalNrOfPages={paginationState.totalNrOfPages}
              handleChangePageNr={handleChangePageNr}
            />
          ) : (
            <>
              {!isLoading && products && (
                <>
                  <h5 className="mt-5">
                    Oops! We couldn't find any products that match your search.
                  </h5>
                  <p>
                    Please try adjusting your filters or check back later. We
                    are adding new items to our inventory every day!
                  </p>
                </>
              )}
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
}
